import { useCallback, useEffect, useRef, useState } from 'react';
import { useIdleTimer } from 'react-idle-timer';
import { i18n } from './i18n';
import keycloak, { getKeycloakRedirectUri } from './keycloak';
import { getStorage, hasOAuthCallbackInUrl, isJustLoggedOut, removeStorage, setStorage, type StoredTokenObject } from './utils/authStorage';
import { invalidateSession, isSessionTimedOut, isStoredTokenValid, hasActiveAuthSession, clearKeycloakSessionInMemory, clearSessionTimeout } from './utils/authSession';
import {
  getLiveAccessTokenExpirySec,
  persistKeycloakAuthState,
  TOKEN_MIN_VALIDITY_SEC,
  TOKEN_REFRESH_AHEAD_SEC,
} from './utils/authToken';
import { isKeycloakOAuthCallback } from './utils/keycloakCallback';
import { SESSION_TIMEOUT_EVENT } from './utils/sessionTimeout';
import type { User } from './services/types';
import { useNavigate, useLocation } from 'react-router-dom';
import { clearStoredActiveAppUser, syncActiveAppUserOnLogin } from './services/activeAppUser';
import { updateAppUserLoginDetailsOnLogin } from './services/userAdministration';
import AppRoutes from './routes/AppRoutes';

const STORED_TOKEN_MIN_VALIDITY_SEC = 60;
const REFRESH_POLL_MS = 30000;

function buildUserFromKeycloak(): User | null {
  if (!keycloak.authenticated || !keycloak.tokenParsed) return null;
  const p = keycloak.tokenParsed as Record<string, unknown>;
  const id = (typeof p.sub === 'string' && p.sub) || (typeof p.sid === 'string' && p.sid) || 'kc-user';
  const displayName =
    (typeof p.name === 'string' && p.name.trim()) ||
    (typeof p.preferred_username === 'string' && p.preferred_username.trim()) ||
    'User';
  // This runs on every token refresh, not just initial login — the real role comes
  // from /active via syncPostLoginSession() and must be preserved here, otherwise a
  // routine background refresh (e.g. right after the tab was idle) wipes an
  // already-known role back to blank with nothing left to restore it.
  const existingRole = getStorage<User>('kcUser')?.role ?? '';
  return { id, displayName, role: existingRole as User['role'] };
}

function persistAuthFromKeycloak(): void {
  persistKeycloakAuthState();
  const u = buildUserFromKeycloak();
  if (u) setStorage('kcUser', u);
}

function isPublicAppPath(pathname: string): boolean {
  const normalized = pathname.replace(/\/$/, '') || '/';
  return (
    normalized.startsWith('/session-timeout') ||
    normalized.startsWith('/unauthorized') ||
    normalized.startsWith('/logged-out')
  );
}

let keycloakDidInit = false;

async function initKeycloakOnce(onLoad: 'login-required' | 'check-sso'): Promise<boolean> {
  if (keycloakDidInit) return true;
  try {
    console.info('[kc] init start', { onLoad, href: window.location.href });
    await keycloak.init({
      onLoad,
      redirectUri: getKeycloakRedirectUri(),
      checkLoginIframe: false,
    });
    keycloakDidInit = true;
    console.info('[kc] init done', { authenticated: keycloak.authenticated, hasToken: Boolean(keycloak.token) });

    if (keycloak.authenticated && keycloak.token) {
      persistAuthFromKeycloak();
    } else if (onLoad === 'login-required') {
      const forceLogin = isJustLoggedOut();
      await keycloak.login({
        redirectUri: getKeycloakRedirectUri(),
        ...(forceLogin ? { prompt: 'login', maxAge: 0 } : null),
      });
    }

    return true;
  } catch (err) {
    console.error('Keycloak init failed:', err);
    return false;
  }
}

let lastSyncedAccessToken: string | null = null;

type PostLoginSyncResult = 'ok' | 'inactive' | 'skipped' | 'failed';

async function syncPostLoginSession(): Promise<PostLoginSyncResult> {
  const token = keycloak.token ?? null;
  if (!keycloak.authenticated || !token) return 'skipped';
  if (lastSyncedAccessToken === token) return 'skipped';
  lastSyncedAccessToken = token;

  let active = await syncActiveAppUserOnLogin();
  if (active.status === 'inactive') {
    lastSyncedAccessToken = null;
    return 'inactive';
  }
  if (active.status === 'failed') {
    // Don't leave this token permanently "synced" on a transient failure (e.g. a
    // post-login race where the app_user record isn't ready yet) — otherwise nothing
    // ever retries for the rest of the session and the role stays stuck on "Loading…"
    // until the user manually reloads the page.
    lastSyncedAccessToken = null;
    return 'failed';
  }

  const tokenParsed = keycloak.tokenParsed as Record<string, unknown> | undefined;
  // Prefer the legacy `session_state` claim; fall back to the OIDC-standard `sid`
  // claim, since not every Keycloak realm/client config emits both.
  const sessionState = tokenParsed?.session_state ?? tokenParsed?.sid;
  const currentSessionState = typeof sessionState === 'string' ? sessionState : null;
  const storedSessionState = getStorage<string>('kcSessionState');
  const newSession = !storedSessionState || storedSessionState !== currentSessionState;
  if (currentSessionState) setStorage('kcSessionState', currentSessionState);

  await updateAppUserLoginDetailsOnLogin({
    id: active.data.id,
    currentLoginIp: 'unknown',
    newSession,
  });

  // The call above may have just reset current_role_id server-side (new_session=true).
  // Re-read /active so we persist the corrected role instead of the stale switched one.
  if (newSession) {
    const refreshed = await syncActiveAppUserOnLogin();
    if (refreshed.status === 'ok') active = refreshed;
  }

  const cur = getStorage<User>('kcUser');
  setStorage('kcUser', {
    id: active.data.id.trim(),
    displayName: active.data.displayName || cur?.displayName || 'User',
    role: (active.data.defaultRole || cur?.role || '') as User['role'],
  });
  return 'ok';
}

function resetPostLoginSession(): void {
  lastSyncedAccessToken = null;
}

function App() {
  const [keycloakInitialized, setKeycloakInitialized] = useState(false);
  const [postLoginSyncDone, setPostLoginSyncDone] = useState(() => !hasActiveAuthSession());
  const [showTimeout, setShowTimeout] = useState(() => getStorage<boolean>('timeOut') === true);
  const initStarted = useRef(false);
  const refreshTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const refreshPendingRef = useRef(false);
  const navigate = useNavigate();
  const location = useLocation();

  const postLoginRetryCountRef = useRef(0);
  const runPostLoginSyncRef = useRef<() => Promise<PostLoginSyncResult>>(async () => 'skipped');

  const runPostLoginSync = useCallback(async (): Promise<PostLoginSyncResult> => {
    const result = await syncPostLoginSession();
    if (result === 'inactive') {
      navigate('/unauthorized', { replace: true });
    }
    if (result === 'failed' && postLoginRetryCountRef.current < 3) {
      // Likely a transient post-login race (e.g. the app_user record isn't provisioned
      // yet) — retry a few times with backoff before leaving the role stuck unresolved.
      postLoginRetryCountRef.current += 1;
      window.setTimeout(() => {
        void runPostLoginSyncRef.current();
      }, 1500 * postLoginRetryCountRef.current);
    } else if (result === 'ok') {
      postLoginRetryCountRef.current = 0;
    }
    setPostLoginSyncDone(true);
    return result;
  }, [navigate]);

  useEffect(() => {
    runPostLoginSyncRef.current = runPostLoginSync;
  }, [runPostLoginSync]);

  const scheduleNextRefresh = useCallback(() => {
    if (refreshTimerRef.current !== null) {
      clearTimeout(refreshTimerRef.current);
      refreshTimerRef.current = null;
    }

    if (getStorage<boolean>('timeOut') === true) return;
    if (!keycloak.authenticated) return;

    const expiresAt = getLiveAccessTokenExpirySec();
    if (!expiresAt) return;

    const nowSec = Math.floor(Date.now() / 1000);
    const refreshInSec = Math.max(0, expiresAt - nowSec - TOKEN_REFRESH_AHEAD_SEC);
    refreshPendingRef.current = true;

    refreshTimerRef.current = setTimeout(async () => {
      refreshTimerRef.current = null;

      if (getStorage<boolean>('timeOut') === true) {
        refreshPendingRef.current = false;
        return;
      }
      if (!keycloak.authenticated) {
        refreshPendingRef.current = false;
        return;
      }

      try {
        await keycloak.updateToken(0);
        if (keycloak.token) {
          persistAuthFromKeycloak();
          refreshPendingRef.current = false;
          scheduleNextRefresh();
        } else {
          refreshPendingRef.current = false;
        }
      } catch {
        refreshPendingRef.current = false;
        invalidateSession();
        setShowTimeout(true);
      }
    }, refreshInSec * 1000);
  }, []);

  useEffect(() => {
    const prevAuthSuccess = keycloak.onAuthSuccess;
    const prevAuthRefreshSuccess = keycloak.onAuthRefreshSuccess;
    const prevAuthRefreshError = keycloak.onAuthRefreshError;
    const prevAuthLogout = keycloak.onAuthLogout;
    const prevTokenExpired = keycloak.onTokenExpired;

    const writeAuthToStorage = () => {
      if (keycloak.authenticated && keycloak.token) {
        persistAuthFromKeycloak();
      }
    };

    keycloak.onAuthSuccess = () => {
      console.info('[kc] onAuthSuccess', { href: window.location.href });
      if (isJustLoggedOut() && !hasOAuthCallbackInUrl()) {
        console.info('[kc] onAuthSuccess ignored: silent SSO while justLoggedOut');
        prevAuthSuccess?.();
        return;
      }
      if (isSessionTimedOut() && !hasOAuthCallbackInUrl()) {
        console.info('[kc] onAuthSuccess ignored: silent SSO while timed out');
        prevAuthSuccess?.();
        return;
      }
      removeStorage('justLoggedOut');
      clearSessionTimeout();
      setShowTimeout(false);
      writeAuthToStorage();
      scheduleNextRefresh();
      setPostLoginSyncDone(false);
      void runPostLoginSync();
      prevAuthSuccess?.();
    };
    keycloak.onAuthRefreshSuccess = () => {
      console.info('[kc] onAuthRefreshSuccess');
      if (isSessionTimedOut()) {
        prevAuthRefreshSuccess?.();
        return;
      }
      writeAuthToStorage();
      scheduleNextRefresh();
      prevAuthRefreshSuccess?.();
    };
    keycloak.onAuthRefreshError = () => {
      console.warn('[kc] onAuthRefreshError');
      invalidateSession();
      setShowTimeout(true);
      prevAuthRefreshError?.();
    };
    keycloak.onTokenExpired = () => {
      if (refreshPendingRef.current) return;
      void (async () => {
        try {
          await keycloak.updateToken(0);
          if (keycloak.token) {
            persistAuthFromKeycloak();
            scheduleNextRefresh();
          } else {
            invalidateSession();
            setShowTimeout(true);
          }
        } catch {
          invalidateSession();
          setShowTimeout(true);
        }
      })();
      prevTokenExpired?.();
    };
    keycloak.onAuthLogout = () => {
      console.info('[kc] onAuthLogout');
      removeStorage('token');
      removeStorage('kcUser');
      clearStoredActiveAppUser();
      resetPostLoginSession();
      setPostLoginSyncDone(true);
      prevAuthLogout?.();
    };

    return () => {
      keycloak.onAuthSuccess = prevAuthSuccess ?? undefined;
      keycloak.onAuthRefreshSuccess = prevAuthRefreshSuccess ?? undefined;
      keycloak.onAuthRefreshError = prevAuthRefreshError ?? undefined;
      keycloak.onTokenExpired = prevTokenExpired ?? undefined;
      keycloak.onAuthLogout = prevAuthLogout ?? undefined;
    };
  }, [scheduleNextRefresh, runPostLoginSync]);

const idleTimeoutMs = (() => {
  const raw = (import.meta.env.VITE_SESSION_IDLE_MINUTES ?? '').trim();
  const n = raw ? Number(raw) : 15;
  const minutes = Number.isFinite(n) && n > 0 ? n : 15;
  return minutes * 60 * 1000;
})();

  useIdleTimer({
    timeout: idleTimeoutMs,
    onIdle: () => {
      if (keycloak.authenticated && !showTimeout) {
        invalidateSession();
        setShowTimeout(true);
      }
    },
    debounce: 500,
  });

  useEffect(() => {
    if (initStarted.current) return;
    initStarted.current = true;

    const init = async () => {
      const storedToken = getStorage<StoredTokenObject>('token');
      const publicPath = isPublicAppPath(window.location.pathname);
      const justLoggedOut = isJustLoggedOut();
      const isTimedOut = getStorage<boolean>('timeOut') === true;
      const oauthCallback = isKeycloakOAuthCallback();

      console.info('[app] init', {
        href: window.location.href,
        pathname: window.location.pathname,
        publicPath,
        hasStoredToken: Boolean(storedToken?.access_token),
        justLoggedOut,
        isTimedOut,
        oauthCallback,
      });

      if (justLoggedOut) {
        try {
          // Do not use check-sso here — it can silently re-authenticate via the Keycloak SSO cookie.
          await keycloak.init({
            redirectUri: getKeycloakRedirectUri(),
            checkLoginIframe: false,
          });
          keycloakDidInit = true;
        } catch (e) {
          console.warn('[kc] init after logout failed (continuing to login)', e);
        }

        if (keycloak.authenticated && keycloak.token) {
          console.info('[kc] interactive login callback processed after logout');
          removeStorage('justLoggedOut');
          clearSessionTimeout();
          setShowTimeout(false);
          persistAuthFromKeycloak();
          scheduleNextRefresh();
          setPostLoginSyncDone(false);
          await runPostLoginSync();
          setKeycloakInitialized(true);
          return;
        }

        console.info('[kc] forcing login prompt after logout');
        await keycloak.login({
          redirectUri: getKeycloakRedirectUri(),
          prompt: 'login',
          maxAge: 0,
        });
        setPostLoginSyncDone(true);
        setKeycloakInitialized(true);
        return;
      }

      if (oauthCallback) {
        const ok = await initKeycloakOnce('check-sso');
        if (keycloak.authenticated && keycloak.token) {
          clearSessionTimeout();
          setShowTimeout(false);
          persistAuthFromKeycloak();
          scheduleNextRefresh();
          setPostLoginSyncDone(false);
          await runPostLoginSync();
        } else {
          setPostLoginSyncDone(true);
        }
        setKeycloakInitialized(ok);
        if (!ok) initStarted.current = false;
        return;
      }

      if (isTimedOut) {
        invalidateSession({ storeRedirect: false });
        setShowTimeout(true);
        try {
          await keycloak.init({
            redirectUri: getKeycloakRedirectUri(),
            checkLoginIframe: false,
          });
          keycloakDidInit = true;
        } catch (e) {
          console.warn('[kc] init while timed out failed', e);
        }
        clearKeycloakSessionInMemory();
        setPostLoginSyncDone(true);
        setKeycloakInitialized(true);
        return;
      }

      if (storedToken?.access_token) {
        setKeycloakInitialized(true);
        const ok = await initKeycloakOnce('check-sso');
        const onSessionTimeoutPath = window.location.pathname.replace(/\/$/, '').startsWith('/session-timeout');

        if (onSessionTimeoutPath && !isTimedOut) {
          // Stale /session-timeout URL without the timeout flag — do not restore auth here.
          setPostLoginSyncDone(true);
          if (!ok) initStarted.current = false;
          return;
        }

        if (keycloak.authenticated && keycloak.token) {
          clearSessionTimeout();
          setShowTimeout(false);
          persistAuthFromKeycloak();
          scheduleNextRefresh();
          setPostLoginSyncDone(false);
          await runPostLoginSync();
        } else if (!isStoredTokenValid(storedToken, STORED_TOKEN_MIN_VALIDITY_SEC) && !publicPath) {
          invalidateSession();
          setShowTimeout(true);
          setPostLoginSyncDone(true);
        } else if (isStoredTokenValid(storedToken, 0)) {
          clearSessionTimeout();
          try {
            await keycloak.updateToken(-1);
            if (keycloak.authenticated && keycloak.token) {
              setShowTimeout(false);
              persistAuthFromKeycloak();
              scheduleNextRefresh();
              setPostLoginSyncDone(false);
              await runPostLoginSync();
            } else {
              setPostLoginSyncDone(true);
            }
          } catch {
            if (!publicPath) {
              invalidateSession();
              setShowTimeout(true);
            }
            setPostLoginSyncDone(true);
          }
        } else {
          setPostLoginSyncDone(true);
        }

        if (!ok) initStarted.current = false;
        return;
      }

      const ok = await initKeycloakOnce(publicPath ? 'check-sso' : 'login-required');
      if (ok && keycloak.authenticated) {
        scheduleNextRefresh();
        setPostLoginSyncDone(false);
        await runPostLoginSync();
      } else {
        setPostLoginSyncDone(true);
      }
      setKeycloakInitialized(ok);
      if (!ok) initStarted.current = false;
    };

    void init();
  }, [scheduleNextRefresh, runPostLoginSync]);

  useEffect(() => {
    const handler = () => {
      setShowTimeout(true);
      navigate('/session-timeout', { replace: true });
    };
    window.addEventListener(SESSION_TIMEOUT_EVENT, handler);
    return () => window.removeEventListener(SESSION_TIMEOUT_EVENT, handler);
  }, [navigate]);

  useEffect(() => {
    if (!keycloakInitialized) return;
    if (!isSessionTimedOut()) return;

    // Drop any in-memory Keycloak session so editing the URL cannot silently re-enter the app.
    clearKeycloakSessionInMemory();

    if (!isPublicAppPath(location.pathname)) {
      navigate('/session-timeout', { replace: true });
    }
  }, [keycloakInitialized, location.pathname, navigate]);

  useEffect(() => {
    const interval = window.setInterval(async () => {
      if (getStorage<boolean>('timeOut') === true) return;
      if (!keycloak.authenticated) return;
      try {
        const refreshed = await keycloak.updateToken(TOKEN_MIN_VALIDITY_SEC);
        if (refreshed && keycloak.token) {
          persistAuthFromKeycloak();
        }
      } catch {
        invalidateSession();
        setShowTimeout(true);
      }
    }, REFRESH_POLL_MS);
    return () => window.clearInterval(interval);
  }, []);

  useEffect(() => {
    const tick = () => {
      if (refreshPendingRef.current) return;
      if (getStorage<boolean>('timeOut') === true) return;
      if (!keycloak.authenticated) return;

      const expiresAt = getLiveAccessTokenExpirySec();
      if (!expiresAt) return;

      const remainingSec = expiresAt - Math.floor(Date.now() / 1000);
      if (remainingSec <= 0) {
        invalidateSession();
        setShowTimeout(true);
      }
    };

    tick();
    const interval = window.setInterval(tick, 1000);
    return () => window.clearInterval(interval);
  }, []);

  useEffect(() => {
    return () => {
      if (refreshTimerRef.current !== null) clearTimeout(refreshTimerRef.current);
    };
  }, []);

  const waitingForPostLoginSync =
    keycloakInitialized &&
    keycloak.authenticated &&
    !postLoginSyncDone &&
    !isPublicAppPath(window.location.pathname);

  if (!keycloakInitialized || waitingForPostLoginSync) {
    return <div>{i18n.t('common.loading')}</div>;
  }

  return <AppRoutes />;
}

export default App;
